import { SingleNews } from '@toggle/toggle';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NewsThumbnail } from '~/components/news-thumbnail/NewsThumbnail';
import {
  DatePillDropdown,
  DatePillDropdownAction,
} from '~/modules/date-pill-dropdown/DatePillDropdown';

import { TEMP_MIN_FETCHING_NEWS_DATE } from '../../utils/utils';
import styles from './NewsGroup.module.scss';

export const SCROLLING_PADDING = 3000;

export interface NewsGroupProps {
  list?: SingleNews[];
  onChangeDate: (action: DatePillDropdownAction) => void;
  onNewsClick: (singleNews: SingleNews) => void;
  pillTime: string;
  isShowDJBadge: boolean;
  groupRef?: (element: HTMLDivElement | null) => void;
}

export const NewsGroup = ({
  list = [],
  onChangeDate,
  onNewsClick,
  pillTime,
  isShowDJBadge,
  groupRef,
}: NewsGroupProps) => {
  const { t } = useTranslation(['news']);

  const [isSticky, setIsSticky] = useState(false);
  const datePillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const refDom = datePillRef.current;
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsSticky(e.intersectionRatio < 1);
      },
      { threshold: [1] }
    );

    if (refDom) {
      observer.observe(refDom);
    }

    return () => {
      refDom && observer.unobserve(refDom);
    };
  }, [isSticky, datePillRef]);

  return (
    <div
      className={styles.newsGroupRoot}
      ref={groupRef}
      data-testid="news-group"
    >
      <div
        className={styles.pillWrapper}
        ref={datePillRef}
        data-testid="top-pill-wrapper"
      >
        <DatePillDropdown
          originalPillTime={pillTime}
          onChangeDate={onChangeDate}
          minDate={TEMP_MIN_FETCHING_NEWS_DATE}
        />
        {isShowDJBadge && (
          <div className={styles.badgeWrapper} data-testid="dow-jones-badge">
            <img
              src="/app/static/images/logotypes/dow-jones-logo.svg"
              alt={t('news:dowJonesDisclaimer.dowJonesLogo')}
            />
            <p className={styles.poweredBy}>{t('news:poweredBy')}</p>
            <img
              className={styles.styledImg}
              src="/app/static/images/logotypes/dow-jones-name.svg"
              alt={t('news:dowJonesDisclaimer.downJonesName')}
            />
          </div>
        )}
        <hr
          className={clsx(styles.divider, isSticky && styles.isHidden)}
          data-testid="group-news-divider"
        />
      </div>

      {list.map(news => {
        return (
          <NewsThumbnail key={news.id} newsData={news} onClick={onNewsClick} />
        );
      })}
    </div>
  );
};
